import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Input, Button, Select, DatePicker, InputNumber, Space, Spin, message,
  Card, Table, Popconfirm, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getQuote, createQuote, updateQuote,
  listQuoteLineItems, createQuoteLineItem, updateQuoteLineItem, deleteQuoteLineItem,
  type QuoteLineItem,
} from '@/api/quoteApi';
import { listCustomers, type Customer } from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'] as const;

interface LineItemRow {
  key: string; // temp key for new rows, or id for existing
  id?: string;
  product_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  line_total: number;
  sort_order: number;
  isNew?: boolean;
  isDirty?: boolean;
}

export default function QuoteForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [nextSortOrder, setNextSortOrder] = useState(1);

  const canWrite = hasPermission('quotes', 'write');

  // Load customers for select
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const result = await listCustomers({ from: 0, size: 20, keyword: customerSearch || undefined });
        setCustomerOptions(
          result.items.map((c: Customer) => ({
            value: c.id,
            label: `${c.name}${c.company ? ` (${c.company})` : ''}`,
          }))
        );
      } catch {
        // silently fail
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // Load quote data for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Promise.allSettled([getQuote(id), listQuoteLineItems(id)])
      .then(([quoteResult, itemsResult]) => {
        if (quoteResult.status === 'fulfilled') {
          const q = quoteResult.value;
          form.setFieldsValue({
            customer_id: q.customer_id,
            contact_id: q.contact_id || undefined,
            opportunity_id: q.opportunity_id || undefined,
            quote_number: q.quote_number || undefined,
            title: q.title,
            description: q.description || undefined,
            status: q.status,
            valid_until: q.valid_until ? dayjs(q.valid_until) : undefined,
            discount_percentage: q.discount_percentage,
            discount_amount: q.discount_amount,
            tax_percentage: q.tax_percentage,
            currency: q.currency,
            payment_terms: q.payment_terms || undefined,
            notes: q.notes || undefined,
            tags: q.tags?.length ? q.tags : undefined,
          });

          // Pre-populate customer option
          setCustomerOptions((prev) => {
            if (prev.find((o) => o.value === q.customer_id)) return prev;
            return [...prev, { value: q.customer_id, label: q.customer_id }];
          });
        }

        if (itemsResult.status === 'fulfilled') {
          const items = itemsResult.value;
          setLineItems(
            items.map((li: QuoteLineItem) => ({
              key: li.id,
              id: li.id,
              product_name: li.product_name,
              description: li.description,
              quantity: li.quantity,
              unit_price: li.unit_price,
              discount_percentage: li.discount_percentage,
              line_total: li.line_total,
              sort_order: li.sort_order,
            }))
          );
          const maxOrder = items.length > 0
            ? Math.max(...items.map((li: QuoteLineItem) => li.sort_order))
            : 0;
          setNextSortOrder(maxOrder + 1);
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, form]);

  const calcLineTotal = (qty: number, price: number, disc: number) =>
    Math.round(qty * price * (1 - disc / 100) * 100) / 100;

  const addLineItem = useCallback(() => {
    const newRow: LineItemRow = {
      key: `new_${Date.now()}`,
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      line_total: 0,
      sort_order: nextSortOrder,
      isNew: true,
    };
    setLineItems((prev) => [...prev, newRow]);
    setNextSortOrder((n) => n + 1);
  }, [nextSortOrder]);

  const updateLineItemField = useCallback(
    (key: string, field: keyof LineItemRow, value: unknown) => {
      setLineItems((prev) =>
        prev.map((row) => {
          if (row.key !== key) return row;
          const updated = { ...row, [field]: value, isDirty: true };
          if (['quantity', 'unit_price', 'discount_percentage'].includes(field)) {
            updated.line_total = calcLineTotal(
              field === 'quantity' ? (value as number) : updated.quantity,
              field === 'unit_price' ? (value as number) : updated.unit_price,
              field === 'discount_percentage' ? (value as number) : updated.discount_percentage
            );
          }
          return updated;
        })
      );
    },
    []
  );

  const removeLineItem = useCallback(
    async (key: string) => {
      const row = lineItems.find((r) => r.key === key);
      if (row?.id && isEdit) {
        try {
          await deleteQuoteLineItem(id, row.id);
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('app.error'));
          return;
        }
      }
      setLineItems((prev) => prev.filter((r) => r.key !== key));
    },
    [lineItems, isEdit, id, t]
  );

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSaving(true);
      try {
        const quoteData = {
          customer_id: values.customer_id as string,
          contact_id: (values.contact_id as string) || undefined,
          opportunity_id: (values.opportunity_id as string) || undefined,
          quote_number: (values.quote_number as string) || undefined,
          title: values.title as string,
          description: (values.description as string) || undefined,
          status: isEdit ? (values.status as string) : undefined,
          valid_until: values.valid_until
            ? (values.valid_until as dayjs.Dayjs).toISOString()
            : undefined,
          discount_percentage: (values.discount_percentage as number) || 0,
          discount_amount: (values.discount_amount as number) || 0,
          tax_percentage: (values.tax_percentage as number) || 0,
          currency: (values.currency as string) || 'TWD',
          payment_terms: (values.payment_terms as string) || undefined,
          notes: (values.notes as string) || undefined,
          tags: values.tags as string[] | undefined,
        };

        let quoteId: string;
        if (isEdit) {
          await updateQuote(id, quoteData);
          quoteId = id;
        } else {
          const created = await createQuote(quoteData);
          quoteId = created.id;
        }

        // Save line items
        for (const row of lineItems) {
          const itemData = {
            product_name: row.product_name,
            description: row.description || undefined,
            quantity: row.quantity,
            unit_price: row.unit_price,
            discount_percentage: row.discount_percentage,
            sort_order: row.sort_order,
          };

          if (row.isNew) {
            await createQuoteLineItem(quoteId, itemData);
          } else if (row.isDirty && row.id) {
            await updateQuoteLineItem(quoteId, row.id, itemData);
          }
        }

        message.success(t(isEdit ? 'quotes.update_success' : 'quotes.create_success'));
        navigate(`/quotes/${quoteId}`);
      } catch (err) {
        message.error(err instanceof Error ? err.message : t('app.error'));
      } finally {
        setSaving(false);
      }
    },
    [isEdit, id, lineItems, navigate, t]
  );

  useEffect(() => {
    if (!canWrite) navigate('/quotes');
  }, [canWrite, navigate]);

  if (!canWrite) return null;

  if (loading) return <Spin size="large" />;

  const lineItemColumns: ColumnsType<LineItemRow> = [
    {
      title: '#',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60,
      render: (val: number, _record: LineItemRow, index: number) => index + 1,
    },
    {
      title: t('quotes.line_item.product_name'),
      dataIndex: 'product_name',
      key: 'product_name',
      render: (val: string, record: LineItemRow) => (
        <Input
          value={val}
          onChange={(e) => updateLineItemField(record.key, 'product_name', e.target.value)}
          placeholder={t('quotes.line_item.product_name')}
        />
      ),
    },
    {
      title: t('quotes.line_item.description'),
      dataIndex: 'description',
      key: 'description',
      render: (val: string, record: LineItemRow) => (
        <Input
          value={val}
          onChange={(e) => updateLineItemField(record.key, 'description', e.target.value)}
        />
      ),
    },
    {
      title: t('quotes.line_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (val: number, record: LineItemRow) => (
        <InputNumber
          value={val}
          min={0.01}
          style={{ width: '100%' }}
          onChange={(v) => updateLineItemField(record.key, 'quantity', v ?? 1)}
        />
      ),
    },
    {
      title: t('quotes.line_item.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      render: (val: number, record: LineItemRow) => (
        <InputNumber
          value={val}
          min={0}
          style={{ width: '100%' }}
          onChange={(v) => updateLineItemField(record.key, 'unit_price', v ?? 0)}
        />
      ),
    },
    {
      title: t('quotes.line_item.discount'),
      dataIndex: 'discount_percentage',
      key: 'discount_percentage',
      width: 100,
      render: (val: number, record: LineItemRow) => (
        <InputNumber
          value={val}
          min={0}
          max={100}
          formatter={(v) => `${v}%`}
          parser={(v) => Number((v ?? '').replace('%', ''))}
          style={{ width: '100%' }}
          onChange={(v) => updateLineItemField(record.key, 'discount_percentage', v ?? 0)}
        />
      ),
    },
    {
      title: t('quotes.line_item.line_total'),
      dataIndex: 'line_total',
      key: 'line_total',
      width: 130,
      align: 'right',
      render: (val: number) => val?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: LineItemRow) => (
        <Popconfirm
          title={t('quotes.line_item_confirm_delete')}
          onConfirm={() => removeLineItem(record.key)}
          okText={t('app.yes')}
          cancelText={t('app.no')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>
        {t(isEdit ? 'quotes.edit_title' : 'quotes.create_title')}
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          currency: 'TWD',
          discount_percentage: 0,
          discount_amount: 0,
          tax_percentage: 0,
        }}
      >
        <Form.Item
          name="customer_id"
          label={t('quotes.field.customer_id')}
          rules={[{ required: true, message: t('quotes.validation.customer_id_required') }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={setCustomerSearch}
            placeholder={t('quotes.select_customer')}
            options={customerOptions}
          />
        </Form.Item>

        <Form.Item name="contact_id" label={t('quotes.field.contact_id')}>
          <Input placeholder={t('quotes.field.contact_id')} />
        </Form.Item>

        <Form.Item name="opportunity_id" label={t('quotes.field.opportunity_id')}>
          <Input placeholder={t('quotes.field.opportunity_id')} />
        </Form.Item>

        <Form.Item name="quote_number" label={t('quotes.field.quote_number')}>
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item
          name="title"
          label={t('quotes.field.title')}
          rules={[
            { required: true, message: t('quotes.validation.title_required') },
            { max: 500, message: t('quotes.validation.title_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('quotes.field.description')}
          rules={[{ max: 2000, message: t('quotes.validation.description_max_length') }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('quotes.field.status')}>
            <Select
              options={QUOTE_STATUSES.map((s) => ({
                value: s,
                label: t(`quotes.status_options.${s}`),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item name="valid_until" label={t('quotes.field.valid_until')}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Space size="large" style={{ width: '100%' }}>
          <Form.Item name="currency" label={t('quotes.field.currency')}>
            <Input maxLength={10} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="discount_percentage" label={t('quotes.field.discount_percentage')}>
            <InputNumber min={0} max={100} />
          </Form.Item>
          <Form.Item name="discount_amount" label={t('quotes.field.discount_amount')}>
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="tax_percentage" label={t('quotes.field.tax_percentage')}>
            <InputNumber min={0} max={100} />
          </Form.Item>
        </Space>

        <Form.Item
          name="payment_terms"
          label={t('quotes.field.payment_terms')}
          rules={[{ max: 1000, message: t('quotes.validation.payment_terms_max_length') }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('quotes.field.notes')}
          rules={[{ max: 2000, message: t('quotes.validation.notes_max_length') }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item name="tags" label={t('quotes.field.tags')}>
          <Select mode="tags" placeholder={t('quotes.field.tags')} />
        </Form.Item>

        {/* Line Items Table */}
        <Card
          title={t('quotes.line_items_title')}
          extra={
            <Button type="dashed" icon={<PlusOutlined />} onClick={addLineItem}>
              {t('quotes.add_line_item')}
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <Table<LineItemRow>
            dataSource={lineItems}
            columns={lineItemColumns}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/quotes/${id}` : '/quotes')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
